/**
 * Totaux déjà calculés, retenus par document.
 *
 * <p>Les écrans redemandent sans cesse les mêmes : le tableau de bord parcourt
 * les factures douze fois pour l'histogramme, puis une fois par client pour le
 * classement. Sur trois ans d'activité cela faisait des centaines de milliers
 * de calculs identiques, refaits à chaque rendu.
 *
 * <p>La clé est l'objet lui-même : une modification en produit un nouveau, qui
 * n'a donc pas d'entrée ici. Rien à invalider.
 */
import { prixApplique } from './paliers';
import { anneeDe } from './cloture';
import { paiementsDe } from './paiements-ecrits';
import { TAUX_LEGAUX_ACTUELS, tauxTvaDeType, tauxTvaEnVigueur } from './tva';

const totauxRetenus = new WeakMap();

/** Totaux d'un document, calculés une fois pour toutes. */
function totauxDe(doc) {
  if (!doc || typeof doc !== 'object') {
    return totauxDocument(doc?.lignes, doc?.tvaPct, doc?.remisePct);
  }
  const connu = totauxRetenus.get(doc);
  if (connu) {
    return connu;
  }
  const calcules = totauxDocument(doc.lignes, doc.tvaPct, doc.remisePct);
  totauxRetenus.set(doc, calcules);
  return calcules;
}

/**
 * Hors-taxe d'un devis ou d'une facture, tel qu'il s'imprime.
 *
 * <p>Plusieurs écrans le recalculaient de leur côté en oubliant la remise
 * globale : le chiffre d'affaires, les impayés, l'échéancier et le décompte de
 * TVA annonçaient donc plus que ce qui a été facturé.
 *
 * @param {object} doc devis ou facture
 * @returns {number} le hors-taxe au centime
 */
export function htDocument(doc) {
  return totauxDe(doc).ht;
}

/**
 * Total toutes taxes d'un devis ou d'une facture, tel qu'il s'imprime.
 *
 * <p>Les mêmes écrans appliquaient le taux du document à l'ensemble du
 * hors-taxe, ce qui fausse tout document mêlant plusieurs taux.
 *
 * @param {object} doc devis ou facture
 * @returns {number} le total TTC au centime
 */
export function ttcDocument(doc) {
  return totauxDe(doc).ttc;
}

/**
 * Hors-taxe d'une vente au comptoir.
 *
 * <p>Les prix du catalogue sont saisis hors taxe : la TVA s'ajoute à
 * l'encaissement, elle n'est pas contenue dans le prix affiché. Une vente est
 * donc enregistrée pour son hors-taxe, comme le hors-taxe d'une facture, et
 * les deux s'additionnent sans conversion.
 *
 * <p>Cette fonction divisait le total par un plus le taux, comme si le prix
 * portait la taxe. Sur une vente de mille francs elle annonçait 925.07 au lieu
 * de 1000 : le chiffre d'affaires du magasin et la base du décompte TVA
 * étaient minorés de la taxe, chaque jour et sur chaque vente.
 *
 * @param {object} vente vente enregistrée à la caisse
 * @returns {number} le hors-taxe au centime
 */
export function htVente(vente) {
  return auCentime(+vente?.total || 0);
}

/**
 * Ce qui reste dû sur une facture.
 *
 * <p>L'accueil et la comptabilité additionnaient le total de toute facture
 * marquée « envoyée », au taux de 8.1 % appliqué en bloc : une facture à
 * plusieurs taux était mal comptée, et une facture déjà payée en partie
 * figurait pour son montant entier, alors qu'elle était par ailleurs signalée
 * en retard.
 *
 * @param {object} facture facture examinée
 * @returns {number} le solde restant, jamais négatif
 */
/**
 * Vrai si la facture compte dans le chiffre d'affaires.
 *
 * <p>Un brouillon n'est pas parti, une facture annulée ne vaut plus : ni l'un
 * ni l'autre n'est une recette. Cinq écrans additionnaient chacun à leur
 * façon, et quatre chiffres d'affaires différents se lisaient pour le même
 * exercice.
 *
 * @param {object} facture facture examinée
 * @returns {boolean} vrai si elle entre dans le chiffre d'affaires
 */
export function compteDansLeCa(facture) {
  if (!facture) return false;
  return facture.statut !== 'Brouillon' && facture.statut !== 'Annulée';
}

/**
 * Signe d'une facture dans une somme : un avoir se retranche.
 *
 * @param {object} facture facture examinée
 * @returns {number} 1, ou -1 pour un avoir
 */
export function signeCa(facture) {
  return facture?.type === 'avoir' ? -1 : 1;
}

export function soldeDu(facture) {
  if (!facture || facture.type === 'avoir') {
    return 0;
  }
  const encaisse = +facture.paidAmount || 0;
  return Math.max(0, auCentime(ttcDocument(facture) - encaisse));
}

/**
 * Arrondi au centime, comme la base le pratique.
 *
 * <p>Le flottant place certaines valeurs — 1.005 par exemple — juste sous la
 * moitié : l'arrondi ordinaire les ramènerait à 1.00 alors que le serveur, qui
 * compte en décimal, retient 1.01. On repasse donc par l'écriture décimale
 * avant de trancher.
 *
 * @param {number} valeur montant à arrondir
 * @returns {number} le montant au centime
 */
export function auCentime(valeur) {
  const x = +valeur;
  if (!Number.isFinite(x)) {
    return 0;
  }
  // Chemin rapide : loin d'une demi-centime, l'arrondi ordinaire donne le même
  // résultat que l'écriture décimale, pour une fraction de son coût. Le
  // tableau de bord appelle cette fonction des centaines de milliers de fois.
  const centaines = x * 100;
  const ecart = Math.abs(centaines - Math.round(centaines));
  if (ecart > 1e-6 && Math.abs(ecart - 0.5) > 1e-6) {
    return Math.round(centaines) / 100;
  }
  // Quinze chiffres significatifs : ce que le flottant porte de sûr. Douze
  // décimales fixes en demandaient trop au-delà de 1'000.– (12345.675 s'y
  // écrivait 12345.674999999999), et la TVA d'une grosse facture différait
  // d'un centime de celle du PDF.
  const signe = x < 0 ? -1 : 1;
  return (signe * Math.round(Number((Math.abs(x) * 100).toPrecision(15)))) / 100;
}

/**
 * Prix d'un article tel que le catalogue client l'affiche.
 *
 * <p>Les prix du stock sont saisis hors taxe. Un particulier compare pourtant
 * des prix TVA comprise : le catalogue qu'on lui envoie peut donc les montrer
 * ainsi, au taux propre à l'article — un livre à 2,6 % ne prend pas 8,1 %.
 *
 * @param {number} ht prix hors taxe
 * @param {number} taux taux de TVA de l'article, en pour cent
 * @param {boolean} ttc vrai pour afficher le prix TVA comprise
 * @returns {number} le prix à afficher, au centime
 */
export function prixCatalogue(ht, taux, ttc) {
  const base = +ht || 0;
  return ttc ? auCentime(base * (1 + (+taux || 0) / 100)) : auCentime(base);
}

/**
 * Arrondi décimal à n décimales, moitié vers le haut, comme la base.
 *
 * @param {number} valeur montant à arrondir
 * @param {number} decimales nombre de décimales
 * @returns {number} le montant arrondi
 */
export function arrondi(valeur, decimales) {
  const n = +valeur || 0;
  const signe = n < 0 ? -1 : 1;
  const [entier, fraction = ''] = Math.abs(n)
    .toFixed(decimales + 6)
    .split('.');
  const garde = fraction.slice(0, decimales);
  const suite = fraction.slice(decimales);
  let base = Number(entier + garde);
  if (suite && suite[0] >= '5') base += 1;
  return (signe * base) / 10 ** decimales;
}

/**
 * Net d'une ligne : quantité × prix unitaire appliqué, remise de ligne déduite.
 *
 * <p>Le prix appliqué tient compte des tarifs dégressifs de la ligne : à
 * partir d'une certaine quantité, le prix unitaire change. Le serveur fait de
 * même ; l'écran annonçait sinon un total que le PDF ne reprenait pas.
 */
function netLigne(ligne) {
  return (+ligne.q || 0) * prixApplique(ligne) * (1 - (+ligne.rem || 0) / 100);
}

/**
 * Taux effectif d'une ligne : le sien, sinon celui du document, sinon le taux
 * normal légal — le serveur fait de même pour une pièce qui n'en porte aucun.
 */
function tauxDe(ligne, tauxDocument) {
  if (ligne.taxe === false) {
    return 0;
  }
  if (ligne.tva != null) {
    return +ligne.tva;
  }
  return tauxDocument == null ? TAUX_LEGAUX_ACTUELS.normal : +tauxDocument;
}

/** Début de la clé d'une ligne qui déduit un acompte : `acompte-<facture>-<rang>`. */
export const PREFIXE_DEDUCTION = 'acompte-';

/**
 * Clé de la ligne qui déduit la ligne `rang` d'une facture d'acompte.
 *
 * @param {*} acompteId facture d'acompte déduite
 * @param {number} rang rang de la ligne dans cette facture
 * @returns {string} la clé
 */
export const cleDeDeduction = (acompteId, rang) => PREFIXE_DEDUCTION + acompteId + '-' + rang;

/**
 * Groupe dans lequel la TVA d'une ligne s'arrondit au centime, comme sur le
 * serveur : les lignes qui déduisent une même facture d'acompte arrondissent
 * ensemble, comme sur cette facture ; toutes les autres, ensemble.
 *
 * @param {object} ligne ligne du document
 * @returns {string} le groupe ('' pour les lignes ordinaires)
 */
export function groupeArrondi(ligne) {
  const cle = String(ligne?.cle || '');
  if (!cle.startsWith(PREFIXE_DEDUCTION)) return '';
  const fin = cle.lastIndexOf('-');
  return fin > PREFIXE_DEDUCTION.length ? cle.slice(0, fin) : cle;
}

/**
 * Totaux d'un devis ou d'une facture, selon la règle du document imprimé.
 *
 * <p>Les écrans calculaient chacun les leurs, et aucun ne suivait celle du
 * serveur : le total affiché différait de celui du PDF et du montant porté par
 * le QR — c'est-à-dire de ce que le client paie. Deux écarts s'y ajoutaient :
 * l'arrondi, appliqué à la somme ici et à chaque tranche là-bas, et la remise
 * globale, que le hors-taxe de certains écrans oubliait alors que la TVA la
 * retenait.
 *
 * <p>La règle retenue est celle du serveur, puisque c'est elle qui s'imprime :
 * le hors-taxe est arrondi une fois, la TVA est la somme des tranches
 * arrondies, et le TTC additionne les deux. Dans une tranche, la déduction
 * d'un acompte s'arrondit à part (voir {@link groupeArrondi}) : l'acompte et
 * la facture finale font alors, au centime près, le total de l'offre.
 *
 * @param {Array} lignes            lignes du document
 * @param {number} [tauxDocument]   taux appliqué aux lignes sans taux propre
 * @param {number} [remiseGlobale]  remise du document, en pourcentage
 * @returns {{ht: number, tva: number, ttc: number, detail: Array, multi: boolean,
 *            htBrut: number, rabaisLignes: number, remise: number}} les totaux
 */
export function totauxDocument(lignes, tauxDocument, remiseGlobale) {
  const liste = lignes || [];
  const multiplicateur = 1 - (+remiseGlobale || 0) / 100;

  const htBrut = liste.reduce((s, l) => s + (+l.q || 0) * prixApplique(l), 0);
  const sousTotal = liste.reduce((s, l) => s + netLigne(l), 0);
  const ht = auCentime(sousTotal * multiplicateur);

  const parTaux = new Map();
  for (const ligne of liste) {
    const taux = tauxDe(ligne, tauxDocument);
    const base = netLigne(ligne) * multiplicateur;
    const cumul = parTaux.get(taux) || { ht: 0, groupes: new Map() };
    cumul.ht += base;
    // Le serveur arrondit la TVA de chaque ligne au dix-millieme avant de
    // sommer : quatre-vingt-dix lignes a 0.05 donnaient 0.36 ici et 0.37 la.
    const g = groupeArrondi(ligne);
    cumul.groupes.set(g, (cumul.groupes.get(g) || 0) + arrondi((base * taux) / 100, 4));
    parTaux.set(taux, cumul);
  }

  const detail = [...parTaux.entries()]
    .filter(([taux]) => taux > 0)
    .sort((a, b) => b[0] - a[0])
    .map(([taux, cumul]) => ({
      taux,
      ht: auCentime(cumul.ht),
      tva: auCentime([...cumul.groupes.values()].reduce((s, t) => s + auCentime(t), 0)),
    }));

  const tva = detail.reduce((s, x) => s + x.tva, 0);
  return {
    htBrut: auCentime(htBrut),
    rabaisLignes: auCentime(htBrut - sousTotal),
    remise: auCentime(sousTotal - sousTotal * multiplicateur),
    ht,
    detail,
    // Sommer des montants deja arrondis reintroduit le bruit du flottant :
    // 36'494.52 s'ecrivait 36'494.520000000004.
    base: auCentime(detail.reduce((s, x) => s + x.ht, 0)),
    tva: auCentime(tva),
    ttc: auCentime(ht + tva),
    multi: detail.length > 1,
  };
}

/**
 * Taux de TVA d'une dépense : celui du type de sa famille, à sa date.
 *
 * <p>Une famille de frais porte un type de taux — normal, réduit, hébergement
 * — et non un nombre : la valeur est celle en vigueur à la date de la dépense,
 * réglée dans Réglages → Documents. Un repas de 2023 se déduit à 2,5 %, le
 * même repas en 2024 à 2,6 %, sans retoucher le référentiel.
 *
 * @param {{type?: string, date?: string}} depense dépense saisie
 * @param {Record<string, {tva?: string|null}>} categories référentiel des familles
 * @param {object} [soc] entreprise, dont on lit l'historique des taux
 * @returns {number} le taux, en pour-cent ; 0 sans droit à déduction
 */
export function tauxDepense(depense, categories, soc) {
  // Le taux lu sur le ticket fait foi ; à défaut, celui de la famille.
  const lu = depense?.tva;
  if (lu !== null && lu !== undefined && lu !== '' && Number.isFinite(+lu)) return +lu;
  return tauxTvaDeType(soc, categories?.[depense?.type]?.tva, depense?.date);
}

/**
 * La TVA saisie ou lue sur une pièce, au plus celle que contient son montant
 * au taux normal du jour, à cinq centimes près : une faute de frappe (15.– de
 * TVA sur un achat de 20.–) faisait récupérer bien plus que possible. Même
 * règle au serveur.
 *
 * @param {number|string} saisie TVA saisie
 * @param {number} montant montant TTC de la pièce
 * @param {object} [soc] entreprise, dont on lit l'historique des taux
 * @param {string} [date] date de la pièce
 * @returns {number} la TVA retenue, au centime
 */
export function tvaSaisiePlafonnee(saisie, montant, soc, date) {
  if (!(montant > 0)) return auCentime(Math.min(+saisie, montant));
  const normal = tauxTvaEnVigueur(soc, date)?.normal || 0;
  // Cinq centimes de marge : un ticket arrondit sa TVA ligne par ligne.
  const plafond = auCentime(montant - montant / (1 + normal / 100) + 0.05);
  return auCentime(Math.min(+saisie, plafond));
}

/**
 * TVA d'une dépense : celle saisie (lue sur le ticket) si elle l'est, sinon
 * extraite du montant au taux de la dépense. Même règle que la position TVA
 * calculée par le serveur.
 *
 * @param {{type?: string, montant?: number, date?: string, tva?: number, montantTva?: number}} depense dépense saisie
 * @param {Record<string, {tva?: string|null}>} categories référentiel des familles
 * @param {object} [soc] entreprise, dont on lit l'historique des taux
 * @returns {number} la TVA récupérable, au centime
 */
export function tvaDeLaDepense(depense, categories, soc) {
  // Un paiement à l'État (TVA reversée, AVS, impôts) n'est pas un achat : il
  // ne porte aucune TVA à récupérer, même si le montant de la TVA reversée a
  // été lu sur le bulletin de l'AFC et noté dans le champ de la TVA — le
  // serveur l'écarte de même.
  if (categories?.[depense?.type]?.etat) return 0;
  const montant = +depense?.montant || 0;
  const saisie = depense?.montantTva;
  if (saisie !== null && saisie !== undefined && saisie !== '' && Number.isFinite(+saisie)) {
    return tvaSaisiePlafonnee(saisie, montant, soc, depense?.date);
  }
  const taux = tauxDepense(depense, categories, soc);
  if (!taux || !montant) return 0;
  return auCentime(montant - montant / (1 + taux / 100));
}

/**
 * TVA récupérable sur les frais, au taux de chaque catégorie.
 *
 * <p>Une dépense porte le taux de sa famille : le taux normal sur le matériel,
 * le taux réduit sur les denrées, le taux spécial sur l'hébergement, et rien
 * du tout sur ce qui n'ouvre pas droit à déduction. Appliquer le taux normal
 * à tout, comme le faisait la synthèse comptable, réclamait à l'AFC une
 * déduction que l'entreprise n'a pas — et en sous-estimait d'autres.
 *
 * <p>Le montant d'une dépense est TTC : la TVA s'en extrait, elle ne s'y
 * ajoute pas.
 *
 * @param {Array<{type?: string, montant?: number, date?: string}>} frais dépenses saisies
 * @param {Record<string, {tva?: string|null}>} categories référentiel des familles
 * @param {object} [soc] entreprise, dont on lit l'historique des taux
 * @returns {number} la TVA récupérable, en francs
 */
export function tvaRecuperable(frais, categories, soc) {
  // Chaque dépense est arrondie au centime avant la somme, comme la ligne
  // qui s'affiche : sinon le total différait d'un centime de la somme des
  // lignes lue à l'écran.
  // La somme est elle-même remise au centime : additionner des montants au
  // centime laisse sinon traîner le bruit du flottant (0.30000000000000004).
  return auCentime(
    (frais || []).reduce((somme, depense) => somme + tvaDeLaDepense(depense, categories, soc), 0),
  );
}

/**
 * Les deux méthodes de décompte admises par l'AFC.
 *
 * <p>`convenues` — contre-prestations convenues — retient la facture dès son
 * émission ; c'est le régime ordinaire, celui qu'on reçoit en s'annonçant.
 * `recues` — contre-prestations reçues — ne retient que ce qui est encaissé,
 * et doit être demandé à l'AFC.
 */
export const METHODES_TVA = {
  convenues: 'Contre-prestations convenues (à la facturation)',
  recues: 'Contre-prestations reçues (à l’encaissement)',
};

/**
 * Date à laquelle la TVA d'une facture est due, à la facturation : sa date
 * comptable (l'exercice choisi), sauf pour une facture d'acompte. Le chiffre
 * d'affaires d'un acompte peut être rattaché à l'année des travaux ; sa TVA,
 * elle, est due quand il est facturé — la règle du serveur
 * (`InvoiceRepository#emisesSurPeriode`).
 *
 * @param {object} facture facture examinée
 * @returns {string} la date, écrite à la suisse
 */
export function dateDeTva(facture) {
  return facture?.type === 'acompte' ? facture.date : facture?.dateCompta || facture?.date;
}

/**
 * La part d'un document comprise entre deux montants encaissés cumulés :
 * de `avant` à `avant + montant`, sur son total TTC.
 *
 * <p>Chaque borne est arrondie au centime, taux par taux, et la part est leur
 * différence : les parts successives d'une facture — l'acompte de décembre et
 * le solde de février, ou trois paiements partiels — redonnent exactement sa
 * TVA, au centime. Arrondir chaque part à part laissait un centime de trop ou
 * de moins sur l'ensemble. Ce qui dépasse le total ne porte pas de TVA : un
 * client qui paie deux fois ne double pas la taxe due.
 *
 * @param {object} totaux totaux du document ({@link totauxDocument})
 * @param {number} avant déjà encaissé, TVA comprise
 * @param {number} montant encaissé maintenant, TVA comprise (négatif : un remboursement)
 * @returns {{tva: number, ht: number, base: number}} la TVA, le hors-taxe et
 *   sa part taxée
 */
export function partDuDocument(totaux, avant, montant) {
  const ttc = +totaux?.ttc || 0;
  if (!(ttc > 0) || !montant) return { tva: 0, ht: 0, base: 0 };
  const borne = (x) => Math.min(ttc, Math.max(0, x));
  const a = borne(+avant || 0);
  const b = borne((+avant || 0) + (+montant || 0));
  const entre = (v) => auCentime(auCentime((v * b) / ttc) - auCentime((v * a) / ttc));
  const detail = totaux.detail || [];
  const tva = auCentime(detail.reduce((s, x) => s + entre(x.tva), 0));
  const base = auCentime(detail.reduce((s, x) => s + entre(x.ht), 0));
  // La part exonérée (lignes à 0 %) suit la même règle.
  const exonere = entre(auCentime((+totaux.ht || 0) - (+totaux.base || 0)));
  return { tva, base, ht: auCentime(base + exonere) };
}

/** Clé de tri d'une date suisse : « 24.12.2025 » → « 2025-12-24 ». */
function cleTri(date) {
  const m = String(date || '').match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  return m ? m[3] + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0') : String(date || '');
}

/**
 * Ce qu'une facture a encaissé, paiement par paiement, comme le serveur le
 * lit pour la méthode des contre-prestations reçues : les paiements inscrits
 * à leur date ; à défaut, le montant perçu à la date de paiement (une facture
 * payée sans montant noté l'est pour son total).
 */
function encaissementsDe(f, t, paiements) {
  const inscrits = paiements?.get?.(f.id) || [];
  if (inscrits.length) return inscrits;
  const paye = +f.paidAmount || 0;
  const percu =
    f.statut === 'Payée' ? (paye > 0 ? paye : t.ttc) : f.statut === 'Partielle' ? paye : 0;
  return percu ? [{ date: f.paidDate || f.date, montant: percu }] : [];
}

/**
 * TVA facturée sur une période, selon la méthode : la règle de la position
 * TVA du serveur (`PositionTvaService`), pour que l'accueil, la Compta et le
 * décompte hors ligne annoncent le même chiffre.
 *
 * <ul>
 *   <li><b>convenues</b> : une facture compte à sa date ({@link dateDeTva}).
 *       Un acompte reçu une année avant sa facture compte à sa date, pour sa
 *       part de la facture, qui garde le reste (LTVA art. 40). Une facture
 *       d'acompte et la facture finale qui la déduit ligne à ligne font
 *       ensemble la TVA de l'offre, sans la compter deux fois.</li>
 *   <li><b>recues</b> : chaque encaissement compte à sa date, pour sa part de
 *       la TVA de la facture — une facture « Partielle » compte ce qui est
 *       reçu, ni rien ni le tout. Le décompte ne retenait que les factures
 *       « Payée », pour leur TVA entière et à la date de la facture.</li>
 *   <li><b>Avoirs</b> : déduits à leur date ; le décompte local les
 *       ignorait. À l'encaissement, l'avoir d'une facture ne se déduit que
 *       pour ce qui est rendu au client : celui qui efface un solde jamais
 *       payé ne retire rien, puisque rien n'en a été déclaré.</li>
 * </ul>
 * Brouillons et factures annulées ne comptent pas.
 *
 * @param {object} options
 * @param {object[]} options.factures factures de la société (toutes, quand
 *   `dansLaPeriode` est donnée)
 * @param {Map<*, {date: string, montant: number}[]>} [options.paiements]
 *   paiements inscrits par facture (`paiementsParFacture`), date suisse
 * @param {string} [options.methode] `convenues` (défaut) ou `recues`
 * @param {(date: string) => boolean} [options.dansLaPeriode] vrai pour une
 *   date de la période ; sans elle, tout ce qui est donné compte
 * @returns {{factures: number, avoirs: number, base: number, exonere: number,
 *   lignes: {facture: object, date: string, tva: number, ht: number}[]}}
 *   la TVA des factures, celle des avoirs (positive, à retrancher), le
 *   hors-taxe net, sa part exonérée et le détail
 */
export function tvaFactureeSurPeriode({ factures, paiements, methode, dansLaPeriode }) {
  const dans = typeof dansLaPeriode === 'function' ? dansLaPeriode : () => true;
  const recues = methode === 'recues';
  const somme = { factures: 0, avoirs: 0, base: 0, exonere: 0 };
  const lignes = [];
  const ajoute = (facture, date, part, signe) => {
    if (!dans(date) || (!part.tva && !part.ht)) return;
    if (signe < 0) somme.avoirs += part.tva;
    else somme.factures += part.tva;
    somme.base += signe * part.ht;
    somme.exonere += signe * (part.ht - part.base);
    lignes.push({ facture, date, tva: signe * part.tva, ht: signe * part.ht });
  };
  const toutes = (factures || []).filter(compteDansLeCa);
  const parId = new Map(toutes.map((f) => [String(f.id), f]));

  for (const f of toutes) {
    const t = totauxDe(f);
    const entier = { tva: t.tva, ht: t.ht, base: t.base };
    if (f.type === 'avoir') {
      const origine = f.avoirDe != null ? parId.get(String(f.avoirDe)) : null;
      if (!recues || !origine || origine.type === 'avoir') {
        ajoute(f, dateDeTva(f), entier, -1);
        continue;
      }
      // À l'encaissement, seul ce qui revient au client se déduit : ce que la
      // facture a reçu au-delà de ce qu'elle vaut, ses avoirs déduits.
      const tO = totauxDe(origine);
      const paye = encaissementsDe(origine, tO, paiements).reduce((s, p) => s + p.montant, 0);
      const freres = toutes
        .filter((x) => x.type === 'avoir' && String(x.avoirDe) === String(origine.id))
        .sort(
          (x, y) =>
            cleTri(dateDeTva(x)).localeCompare(cleTri(dateDeTva(y))) || (+x.id || 0) - (+y.id || 0),
        );
      const creditAvant = freres
        .slice(0, freres.indexOf(f))
        .reduce((s, x) => s + totauxDe(x).ttc, 0);
      const rendu = Math.min(t.ttc, Math.max(0, paye - (tO.ttc - creditAvant - t.ttc)));
      ajoute(f, dateDeTva(f), partDuDocument(t, 0, auCentime(rendu)), -1);
      continue;
    }
    if (recues) {
      let cumul = 0;
      for (const p of encaissementsDe(f, t, paiements)) {
        ajoute(f, p.date, partDuDocument(t, cumul, p.montant), 1);
        cumul += p.montant;
      }
      continue;
    }
    const date = dateDeTva(f);
    if (!(t.ttc > 0)) {
      ajoute(f, date, entier, 1);
      continue;
    }
    const annee = anneeDe({ date });
    let cumul = 0;
    const anterieurs = paiementsDe(f, paiements?.get?.(f.id))
      .filter((p) => p.montant > 0 && anneeDe({ date: p.date }) < annee)
      .sort((x, y) => cleTri(x.date).localeCompare(cleTri(y.date)));
    for (const p of anterieurs) {
      ajoute(f, p.date, partDuDocument(t, cumul, p.montant), 1);
      cumul += p.montant;
    }
    ajoute(f, date, partDuDocument(t, cumul, t.ttc), 1);
  }
  return {
    factures: auCentime(somme.factures),
    avoirs: auCentime(somme.avoirs),
    base: auCentime(somme.base),
    exonere: auCentime(somme.exonere),
    lignes,
  };
}

/**
 * Décompte de TVA d'une période, selon la méthode de l'entreprise.
 *
 * <p>La méthode était figée à l'encaissement, alors que la plupart des
 * entreprises décomptent à la facturation : leur décompte restait à zéro tant
 * qu'un client n'avait pas payé, et sous-déclarait la période.
 *
 * <p>Les ventes au comptoir comptent aussi. Le décompte ne voyait que les
 * factures : la TVA encaissée au magasin n'était jamais déclarée, alors que
 * le client l'avait payée et que l'entreprise la doit. Une vente est réglée
 * sur-le-champ ; elle entre donc dans le décompte des deux méthodes, à sa
 * date, sans attendre d'encaissement.
 *
 * <p>Les factures suivent {@link tvaFactureeSurPeriode} : avoirs déduits,
 * paiements partiels comptés pour leur part, acompte d'une autre année à sa
 * date — comme la position TVA du serveur, que ce décompte remplace hors
 * ligne.
 *
 * @param {object} options données du décompte
 * @param {Array} options.factures factures de la période, ou toutes avec `dansLaPeriode`
 * @param {Map} [options.paiements] paiements inscrits par facture, date suisse
 * @param {(date: string) => boolean} [options.dansLaPeriode] vrai pour une date de la période
 * @param {Array} [options.ventes] ventes au comptoir de la période
 * @param {number} [options.tauxVentes] taux à retenir pour une vente sans taux propre
 * @param {Array} [options.frais] dépenses de la période
 * @param {Record<string, {tva?: string|null}>} [options.categories] familles de frais
 * @param {object} [options.soc] entreprise, dont on lit l'historique des taux
 * @param {string} [options.methode] `convenues` (défaut) ou `recues`
 * @returns {{base: number, exonere: number, collectee: number, avoirs: number,
 *            deductible: number, nette: number}}
 *          la base, sa part exonérée à 0 %, la TVA collectée (avoirs déduits),
 *          celle des avoirs, la déductible et le solde dû
 */
export function decompteTva({
  factures,
  paiements,
  dansLaPeriode,
  ventes,
  tauxVentes,
  frais,
  categories,
  soc,
  methode,
}) {
  // La TVA d'une facture est celle de ses lignes : appliquer le taux du
  // document a l'ensemble du hors-taxe fausse toute facture qui en mele
  // plusieurs — un menuisier qui livre du bois et pose une cuisine, par
  // exemple.
  const facturee = tvaFactureeSurPeriode({ factures, paiements, methode, dansLaPeriode });

  // Chaque vente porte le taux qui lui a ete applique, fige au moment du
  // passage en caisse : un magasin d'alimentation vend une caisse d'eau a
  // 2,6 % et un ustensile a 8,1 % dans la meme journee, et un taux unique
  // declarait a l'AFC une somme qui n'etait celle d'aucun des deux. Un avoir
  // est une vente negative : il se retranche de lui-meme.
  const defaut = tauxVentes == null ? TAUX_LEGAUX_ACTUELS.normal : +tauxVentes || 0;
  const comptoir = (ventes || []).reduce(
    (cumul, v) => {
      const ht = htVente(v);
      const taux = v?.tx != null ? +v.tx : defaut;
      return {
        ht: cumul.ht + ht,
        tva: cumul.tva + (ht * taux) / 100,
        exonere: cumul.exonere + (taux > 0 ? 0 : ht),
      };
    },
    { ht: 0, tva: 0, exonere: 0 },
  );
  const htComptoir = auCentime(comptoir.ht);
  const tvaComptoir = auCentime(comptoir.tva);

  const base = auCentime(facturee.base + htComptoir);
  const collectee = auCentime(facturee.factures - facturee.avoirs + tvaComptoir);
  const deductible = auCentime(tvaRecuperable(frais, categories, soc));
  return {
    base,
    // L'assiette exoneree se declare a part (chiffre 200 puis 230 du
    // decompte) : elle entre dans le chiffre d'affaires sans porter de taxe.
    exonere: auCentime(facturee.exonere + auCentime(comptoir.exonere)),
    collectee,
    avoirs: facturee.avoirs,
    deductible,
    nette: auCentime(collectee - deductible),
  };
}

/**
 * Remise globale qui fait retomber un document sur un total TTC donné.
 *
 * <p>Une offre reprise d'un ancien logiciel affiche un total TTC rond —
 * 39'000.00 — obtenu par une remise en francs (3'462.30). La même remise en
 * pour-cent, recalculée ici avec la TVA arrondie par taux, tombait à un
 * centime près. On cherche donc, à huit décimales, la remise dont le total
 * est exactement celui de la pièce d'origine. Un écart de plus d'un franc
 * signale des lignes mal lues : la remise lue est alors gardée telle quelle.
 *
 * @param {Array} lignes lignes du document
 * @param {number} tauxDocument taux appliqué aux lignes sans taux propre
 * @param {number} remiseLue remise lue, en pourcentage
 * @param {number} totalTtc total TTC de la pièce d'origine
 * @returns {number} la remise, en pourcentage à huit décimales
 */
export function remiseVisantLeTotal(lignes, tauxDocument, remiseLue, totalTtc) {
  const cible = +totalTtc || 0;
  const lue = +remiseLue || 0;
  const ttc = (p) => totauxDocument(lignes, tauxDocument, p).ttc;
  if (cible <= 0 || lue <= 0 || ttc(lue) === cible || Math.abs(ttc(lue) - cible) > 1) {
    return lue;
  }
  // Le TTC baisse quand la remise monte. On cherche où il passe sous la
  // cible, puis où il la quitte, et l'on prend le milieu : au bord, le serveur
  // pourrait arrondir le centime autrement que l'écran.
  const bord = (atteint) => {
    let bas = 0;
    let haut = 100;
    for (let i = 0; i < 80; i += 1) {
      const milieu = (bas + haut) / 2;
      if (atteint(ttc(milieu))) haut = milieu;
      else bas = milieu;
    }
    return haut;
  };
  const debut = bord((x) => x <= cible);
  const fin = bord((x) => x < cible);
  const p = Math.round(((debut + fin) / 2) * 1e8) / 1e8;
  if (ttc(p) === cible) return p;
  return lue;
}

/**
 * Pourcentage lisible : deux décimales au plus, sans zéros inutiles.
 *
 * <p>Une remise se garde à huit décimales pour tomber juste au centime ;
 * « Remise 8.75642388 % » n'en est pas plus clair pour le client.
 *
 * @param {number} pct pourcentage
 * @returns {string} le pourcentage écrit, « 8.76 » ou « 10 »
 */
export function pourcentLisible(pct) {
  return String(arrondi(+pct || 0, 2));
}

/**
 * Montant TTC de l'acompte d'une offre, au centime près comme le serveur.
 *
 * <p>Le serveur ne prend pas le pourcentage du total TTC : il fait une ligne
 * d'acompte par taux de TVA, égale à la part convenue des lignes à ce taux
 * (arrondie au centime), puis applique la remise globale et la TVA, comme à
 * une facture — c'est la facture d'acompte. L'écran calculait 35 % du TTC :
 * 13'649.47 sur le document et son QR, 13'649.46 dans l'e-mail et sur la
 * facture d'acompte. Les deux suivent désormais la même règle.
 *
 * @param {Array} lignes lignes de l'offre
 * @param {number} [tauxDocument] taux appliqué aux lignes sans taux propre
 * @param {number} [remiseGlobale] remise de l'offre, en pourcentage
 * @param {number} [pourcentage] part de l'acompte, en pourcentage
 * @param {number} [montantFixe] acompte écrit en francs, TVA comprise : il
 *   fait foi, borné au total de l'offre (le serveur le facture au centime)
 * @returns {number} le montant TTC de l'acompte ; 0 sans acompte
 */
export function acompteDocument(lignes, tauxDocument, remiseGlobale, pourcentage, montantFixe) {
  if (+montantFixe > 0) {
    const total = totauxDocument(lignes || [], tauxDocument, remiseGlobale).ttc;
    return arrondi(Math.min(+montantFixe, Math.max(0, total)), 2);
  }
  const part = +pourcentage || 0;
  if (part <= 0) return 0;
  const parTaux = new Map();
  for (const ligne of lignes || []) {
    const exoneree = ligne.taxe === false;
    const taux = exoneree ? 0 : tauxDe(ligne, tauxDocument);
    const cle = exoneree ? 'exoneree' : String(taux);
    const cumul = parTaux.get(cle) || { taux, exoneree, net: 0 };
    cumul.net += netLigne(ligne);
    parTaux.set(cle, cumul);
  }
  const lignesAcompte = [...parTaux.values()].map((c) => ({
    q: 1,
    p: arrondi((c.net * part) / 100, 2),
    rem: 0,
    tva: c.exoneree ? null : c.taux,
    taxe: !c.exoneree,
  }));
  return totauxDocument(lignesAcompte, tauxDocument, remiseGlobale).ttc;
}
